import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { errorMessage } from '../lib/http';
import { SUBMISSION_STATUS, formatDateTime } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, FIELD, FIELD_STYLE, LABEL } from '../lib/ui';
import type { PersonRef } from '../types/workspace';
import { Card, EmptyState, Spinner, StatusPill } from './ui';
import ConfirmDialog from './ConfirmDialog';
import Select from './Select';

export type Lead = {
  id: string;
  createdAt: string;
  emailSent: boolean;
  reportId: string | null;
  status: string;
  statusUpdatedAt: string | null;
  offerAmount: string | null;
  offerTerms: string | null;
  note: string | null;
  partner: { id: string; name: string; email: string; firm: string | null };
  sentBy: PersonRef | null;
  messages?: { id: string; body: string; fromPartner: boolean; createdAt: string; author: (PersonRef & { role: 'ADMIN' | 'CUSTOMER' }) | null }[];
  activities?: unknown[];
};

const PAGE_SIZE = 5;

/** Simple status + comment for partners already emailed. Updates go to the company when sent. */
export default function PartnerLeads({
  companyId,
  leads,
  versionOf,
  onChanged,
  onError,
}: {
  companyId: string;
  leads: Lead[];
  versionOf: (reportId: string | null) => number | undefined;
  onChanged: (message?: string) => void;
  onError: (message: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(leads[0]?.id || null);
  const lead = leads.find((l) => l.id === selected) || null;
  const [status, setStatus] = useState('SENT');
  const [comment, setComment] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirmNotify, setConfirmNotify] = useState(false);

  useEffect(() => {
    if (!leads.some((l) => l.id === selected)) setSelected(leads[0]?.id || null);
  }, [leads, selected]);

  useEffect(() => {
    setStatus(lead?.status || 'SENT');
    setComment(lead?.note || '');
  }, [lead?.id, lead?.status, lead?.note]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const st = SUBMISSION_STATUS[l.status]?.label || l.status;
      return [l.partner.name, l.partner.firm, l.partner.email, st, l.note].some((v) => v?.toLowerCase().includes(q));
    });
  }, [leads, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  useEffect(() => setPage(1), [query]);

  const dirty = Boolean(lead && (status !== lead.status || comment !== (lead.note || '')));

  const save = async (notifyCompany: boolean) => {
    if (!lead) return;
    setBusy(true);
    try {
      await api.patch(`/admin/companies/${companyId}/submissions/${lead.id}`, {
        status,
        note: comment || null,
        notifyCompany,
      });
      setConfirmNotify(false);
      onChanged(notifyCompany ? 'Status sent to the company.' : 'Status saved.');
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!leads.length) {
    return <EmptyState title="No partners emailed yet" body="Send a cold email above. Status and comments for each partner will show here." />;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl px-4 py-3" style={{ background: '#fffafc', border: '1px solid #fce7f3' }}>
        <p className="text-sm text-[#6b3a52]">
          After a cold email is sent, set a simple status and comment. Send that update to the company — they see it on their Partners page (and in Messages).
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
        <Card title="Partners contacted" subtitle="Status and when it was last updated.">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search partner, firm, status…"
            className={`${FIELD} mb-3`}
            style={FIELD_STYLE}
          />
          <ul className="max-h-96 space-y-2 overflow-y-auto">
            {paged.length === 0 && <li className="text-sm text-[#c084a0]">No partners match this search.</li>}
            {paged.map((l) => {
              const st = SUBMISSION_STATUS[l.status];
              const when = l.statusUpdatedAt || l.createdAt;
              const version = versionOf(l.reportId);
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(l.id)}
                    className="w-full rounded-xl px-3 py-2 text-left hover:bg-[#fff5f8]"
                    style={{ border: `1px solid ${l.id === selected ? '#d6006e' : '#fce7f3'}`, background: l.id === selected ? '#fdf2f8' : '#fff' }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-[#6b0030]">
                        {l.partner.name}
                        {l.partner.firm ? ` · ${l.partner.firm}` : ''}
                      </p>
                      <StatusPill label={st?.label || l.status} tone={st?.tone || 'slate'} />
                    </div>
                    <p className="text-[11px] text-[#c084a0]">
                      {formatDateTime(when)}
                      {version ? ` · rating v${version}` : ''}
                      {l.emailSent ? '' : ' · email failed'}
                    </p>
                    {l.note ? <p className="mt-1 truncate text-[11px] text-[#6b3a52]">{l.note}</p> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {filtered.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#b0708f]">
              <span>
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Prev
                </button>
                <span className="px-1 font-semibold text-[#6b0030]">
                  {safePage}/{pageCount}
                </span>
                <button
                  type="button"
                  className={BTN_SOFT}
                  style={BTN_SOFT_STYLE}
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>

        {lead && (
          <Card
            title={`${lead.partner.name}${lead.partner.firm ? ` · ${lead.partner.firm}` : ''}`}
            subtitle={`${lead.partner.email} · emailed ${formatDateTime(lead.createdAt)}`}
          >
            <label className={LABEL}>Status</label>
            <Select
              size="sm"
              value={status}
              disabled={busy}
              onChange={setStatus}
              options={Object.entries(SUBMISSION_STATUS).map(([k, v]) => ({ value: k, label: v.label }))}
              aria-label="Partner status"
            />

            <label className={`${LABEL} mt-3`}>Comment for the company</label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className={`${FIELD} resize-y`}
              style={FIELD_STYLE}
              placeholder="Short update the company should see…"
            />

            <p className="mt-2 text-[11px] text-[#c084a0]">
              Last update: {formatDateTime(lead.statusUpdatedAt || lead.createdAt)}
              {lead.emailSent ? ' · email delivered' : ' · email not delivered'}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={busy || !dirty} onClick={() => void save(false)} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
                {busy && <Spinner className="h-3.5 w-3.5" />} Save
              </button>
              <button type="button" disabled={busy || !dirty} onClick={() => setConfirmNotify(true)} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
                {busy && <Spinner />} Send to company
              </button>
            </div>
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={confirmNotify}
        title="Send update to the company?"
        description={`The company will see “${SUBMISSION_STATUS[status]?.label || status}”${comment.trim() ? ` with your comment` : ''} on their Partners page and in Messages.`}
        confirmLabel="Send to company"
        cancelLabel="Cancel"
        busy={busy}
        onCancel={() => !busy && setConfirmNotify(false)}
        onConfirm={() => void save(true)}
      />
    </div>
  );
}
